import { OpenInNew1OutlineIcon, RevertOutlineIcon } from '@carboncopyco/icons';
import { TextCaption } from '@carboncopyco/ui';
import classNames from 'classnames';

type TypeBasedCellProps = {
  value: any;
  onClick: (type: 'boolean' | 'array' | 'null' | 'object' | 'string') => void;
  onRevert: () => void;
  isModified: boolean;
  isDisabled: boolean;
};

const ObjectButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <button
      type="button"
      className={classNames([
        'grow',
        'flex',
        'items-center',
        'justify-between',
        'gap-2',
        'px-2',
        'h-full',
        'text-ds-natural-600',
        'bg-transparent',
        'cursor-pointer',
        'hover:text-ds-natural-500',
        'focus:text-ds-natural-500',
        'active:text-ds-natural-400',
        'disabled:text-ds-natural-200',
        'disabled:pointer-events-none',
        'disabled:cursor-not-allowed',
        'disabled:select-none',
        'group',
      ])}
      onClick={onClick}
    >
      <TextCaption
        color="text-current"
        label="Database table view"
        ellipsis
      />
      <span className="opacity-0 group-hover:opacity-70">
        <OpenInNew1OutlineIcon size={16} />
      </span>
    </button>
  );
};

export const TypeBasedCell = ({
  value,
  onClick,
  onRevert,
  isModified,
  isDisabled,
}: TypeBasedCellProps) => {
  const textColor = isDisabled ? 'text-ds-natural-300' : 'text-ds-natural-600';

  if (Array.isArray(value)) {
    return (
      <div className="pb-0 pt-px px-2 h-[27px] w-full flex items-center max-w-full cursor-not-allowed">
        <TextCaption
          color={textColor}
          ellipsis
          label="List"
        />
      </div>
    );
  }

  if (typeof value === 'object' && value !== null) {
    return (
      <div className="pb-0 pt-px h-[27px] w-full flex items-center max-w-full hover:bg-ds-natural-100">
        <ObjectButton
          onClick={isModified ? undefined : () => onClick('object')}
        />
      </div>
    );
  }

  return (
    <div
      className={classNames(
        'pb-0 pt-px px-2 h-[27px] w-full flex items-center max-w-full',
        {
          'cursor-not-allowed': isDisabled,
        }
      )}
      onClick={isModified || isDisabled ? undefined : () => onClick('string')}
    >
      <TextCaption
        color={textColor}
        ellipsis
        label={String(value)}
      />
      {isModified && (
        <div className="absolute right-0 top-0 bottom-0 flex items-center justify-center hidden group-hover:flex backdrop-blur-3xl">
          <button
            type="button"
            className="grow flex items-center justify-between px-1.5 h-full text-ds-natural-600 bg-transparent cursor-pointer active:text-ds-natural-400 opacity-70"
            onClick={() => onRevert()}
          >
            <RevertOutlineIcon size={16} />
          </button>
        </div>
      )}
    </div>
  );
};
